import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorHandler } from 'api/axios_instance';
import { CreateWorkflowRequest, QueryKey, UpdateWorkflowMetadataRequest, Workflow } from 'api/model.ts';
import {
  executeWorkflow,
  fillLLMTestData,
  getWorkflow,
  removeWorkflow,
  restoreWorkflowRevision,
  updateWorkflow,
  updateWorkflowDataProtection,
  updateWorkflowMetadata,
} from 'api/workflows/requests';
import { toast } from 'react-toastify';

const queryKey = QueryKey.Workflows;

type Props = {
  projectId: string;
  workflowId: string;
  workflowRevision?: string | number | null;
  workflow?: Workflow;
};

const useWorkflow = ({ projectId, workflowId, workflowRevision = null, workflow: initialWorkflow }: Props) => {
  const queryClient = useQueryClient();

  const { data: latestWorkflow, isLoading } = useQuery({
    queryKey: [queryKey, { workflowId, workflowRevision }],
    queryFn: () => getWorkflow(workflowId, workflowRevision),
    enabled: !!workflowId && !initialWorkflow,
  });

  const workflow = latestWorkflow ?? initialWorkflow;

  const execute = useMutation({
    mutationFn: ({ testMode, parameters }: { testMode: boolean; parameters: Record<string, string> }) =>
      executeWorkflow(workflowId, workflowRevision, testMode, parameters),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QueryKey.WorkflowExecutions, { projectId }] }),
    onError: errorHandler('Executing workflow failed'),
  });

  const update = useMutation({
    mutationFn: (workflow: CreateWorkflowRequest) => updateWorkflow(projectId, workflowId, workflow),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Workflow successfully updated');
    },
    onError: errorHandler('Updating workflow failed'),
  });

  const updateSilent = useMutation({
    mutationFn: (workflow: CreateWorkflowRequest) => updateWorkflow(projectId, workflowId, workflow),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const updateMetadata = useMutation({
    mutationFn: (request: UpdateWorkflowMetadataRequest) => updateWorkflowMetadata(projectId, workflowId, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Workflow successfully updated');
    },
    onError: errorHandler('Updating workflow metadata failed'),
  });

  const updateDataProtection = useMutation({
    mutationFn: (dataProtected: boolean) =>
      updateWorkflowDataProtection(projectId, workflowId, { data_protected: dataProtected }),
    onSuccess: (workflow) => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success(`Workflow data protection ${workflow.data_protected ? 'enabled' : 'disabled'}`);
    },
    onError: errorHandler('Updating workflow data protection failed'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => removeWorkflow(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Workflow successfully removed');
    },
    onError: errorHandler('Removing workflow failed'),
  });

  const fillTestData = useMutation({
    mutationFn: () => fillLLMTestData(workflowId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const restoreRevision = useMutation({
    mutationFn: (revision: number) => restoreWorkflowRevision(workflowId, revision),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey, { workflowId }] });
      toast.success('Workflow revision successfully restored');
    },
    onError: errorHandler('Restoring workflow revision failed'),
  });

  const clearCache = () => {
    void queryClient.invalidateQueries({ queryKey: [queryKey, { workflowId, workflowRevision }] });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.WorkflowExecutions, { workflowId }] });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.WorkflowExecutionsInfinite, { projectId, workflowId }] });
  };

  return {
    workflow,
    isLoading,
    execute,
    update,
    updateSilent,
    updateMetadata,
    updateDataProtection,
    remove,
    fillTestData,
    restoreRevision,
    clearCache,
  };
};

export default useWorkflow;
